import React, { useState, useEffect,useRef } from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Paper,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Button,
  Chip,
  alpha,
  useTheme,
  Link,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Info as InfoIcon,
  CheckCircle as SuccessIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Radio as SSEIcon,
  Close as CloseIcon,
  MarkEmailRead as MarkReadIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Launch as LaunchIcon,
  Refresh as RefreshIcon, // Import RefreshIcon
} from '@mui/icons-material';
import { useNotifications, Notification } from './NotificationContext';
import { formatDistanceToNow } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import AuthService from '../../../services/AuthService';

interface NotificationCenterProps {
  'data-tour'?: string;
}


export default function NotificationCenter(props: NotificationCenterProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const { notifications, unreadCount, markAsRead, markAllAsRead, removeNotification, clearAll, addNotification } = useNotifications();
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    async function getAuthHeaders() {
      const authService = new AuthService();
      const token = await authService.getAccessToken();
      
      if (!token) {
        throw new Error("No access token available");
      }
      
      return {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
    }
    

    const open = Boolean(anchorEl);

    const getNotificationIcon = (type: Notification['type']) => {
        switch (type) {
            case 'success':
                return <SuccessIcon color="success" />;
            case 'warning':
                return <WarningIcon color="warning" />;
            case 'error':
                return <ErrorIcon color="error" />;
            case 'sse':
                return <SSEIcon color="primary" />;
            default:
                return <InfoIcon color="info" />;
        }
    };

    const getNotificationColor = (type: Notification['type']) => {
        switch (type) {
            case 'success':
                return theme.palette.success.main;
            case 'warning':
                return theme.palette.warning.main;
            case 'error':
                return theme.palette.error.main;
            case 'sse':
                return theme.palette.primary.main;
            default:
                return theme.palette.info.main;
        }
    };

    const handleNotificationClick = (notification: Notification) => {
        if (!notification.read) {
            markAsRead(notification.id);
        }
    };

    const handlePatientClick = (patientId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        navigate(`/molemapping/${patientId}`);
        handleClose();
    };

    const extractPatientId = (data: any): string | null => {
        if (!data) return null;
        return data.patientId || null;
    };

    const renderNotificationData = (notification: Notification) => {
        if (!notification.data) return null;

        const patientId = extractPatientId(notification.data);

        return (
            <Box sx={{
                p: 1,
                borderRadius: 1,
                backgroundColor: alpha(theme.palette.background.paper, 0.5),
                border: `1px solid ${theme.palette.divider}`,
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                maxHeight: 150,
                overflow: 'auto',
                mt: 1
            }}>
                {/* Patient ID with clickable link */}
                {patientId && (
                    <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon sx={{ fontSize: '1rem', color: theme.palette.primary.main }} />
                        <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                            Patient:
                        </Typography>
                        <Button
                            size="small"
                            variant="outlined"
                            startIcon={<LaunchIcon sx={{ fontSize: '0.8rem' }} />}
                            onClick={(e) => handlePatientClick(patientId, e)}
                            sx={{
                                minHeight: '20px',
                                fontSize: '0.7rem',
                                padding: '2px 6px',
                                borderRadius: 1,
                                textTransform: 'none',
                                fontFamily: 'monospace',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                },
                            }}
                        >
                            {patientId.substring(0, 8)}...
                        </Button>
                    </Box>
                )}

                {/* Timestamp with quick navigation */}
                {notification.data.timestamp && (
                    <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                            Timestamp:
                        </Typography>
                        <Button
                            size="small"
                            variant="text"
                            onClick={(e) => {
                                e.stopPropagation();
                                if (patientId) {
                                    navigate(`/molemapping/${patientId}`);
                                    handleClose();
                                }
                            } }
                            sx={{
                                minHeight: '18px',
                                fontSize: '0.65rem',
                                padding: '1px 4px',
                                borderRadius: 1,
                                textTransform: 'none',
                                color: theme.palette.info.main,
                                backgroundColor: alpha(theme.palette.info.main, 0.1),
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.info.main, 0.2),
                                    textDecoration: 'underline',
                                },
                            }}
                        >
                            {new Date(notification.data.timestamp).toLocaleTimeString()} →
                        </Button>
                    </Box>
                )}

                {/* Event Type for SSE events - only show if it's TEST_EVENT */}
                {notification.data.eventType === 'TEST_EVENT' && (
                    <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'medium', color: 'text.primary' }}>
                            Event:
                        </Typography>
                        <Chip
                            label="TEST EVENT"
                            size="small"
                            sx={{
                                height: 18,
                                fontSize: '0.65rem',
                                backgroundColor: alpha(theme.palette.secondary.main, 0.1),
                                color: theme.palette.secondary.main,
                                cursor: 'pointer',
                                '&:hover': {
                                    backgroundColor: alpha(theme.palette.secondary.main, 0.2),
                                },
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                if (patientId) {
                                    navigate(`/molemapping/${patientId}`);
                                    handleClose();
                                }
                            } } />
                    </Box>
                )}

                {/* Only show raw data for non-test events or if user specifically wants to see it */}
                {notification.data.eventType !== 'TEST_EVENT' && (
                    <details style={{ marginTop: '8px' }}>
                        <summary style={{
                            cursor: 'pointer',
                            fontSize: '0.7rem',
                            color: theme.palette.text.secondary,
                            userSelect: 'none'
                        }}>
                            View Raw Data
                        </summary>
                        <pre style={{
                            margin: '4px 0 0 0',
                            whiteSpace: 'pre-wrap',
                            fontSize: '0.65rem',
                            color: theme.palette.text.secondary,
                            maxHeight: '100px',
                            overflow: 'auto'
                        }}>
                            {JSON.stringify(notification.data, null, 2)}
                        </pre>
                    </details>
                )}
            </Box>
        );
    };
// #####ADDED THIS FOR RECENT ACTIVITY ENDPOINT #####

    // Use a ref to persist the Set across re-renders
    const processedActivityIdsRef = useRef<Set<string>>(new Set());

    useEffect(() => {
        
        // Load previously processed IDs from localStorage
        try {
            const stored = localStorage.getItem('processedNotificationIds');
            if (stored) {
                const storedArray = JSON.parse(stored);
                processedActivityIdsRef.current = new Set(storedArray);
                console.log('[NotificationCenter] Loaded', storedArray.length, 'processed IDs from localStorage');
            }
        } catch (error) {
            console.warn('[NotificationCenter] Failed to load processed IDs:', error);
        }
        
        // Clean up old IDs (older than 1 hour) to prevent localStorage bloat
        const cleanupOldIds = () => {
            const oneHourAgo = Date.now() - (60 * 60 * 1000);
            const cleaned = new Set<string>();
            
            processedActivityIdsRef.current.forEach(id => {
                // Extract timestamp from ID (assuming format: type_mappingId_timestamp)
                const parts = id.split('_');
                if (parts.length >= 3) {
                    const timestampStr = parts[parts.length - 1];
                    try {
                        const timestamp = new Date(timestampStr).getTime();
                        if (timestamp > oneHourAgo) {
                            cleaned.add(id);
                        }
                    } catch (e) {
                        // Keep the ID if we can't parse the timestamp
                        cleaned.add(id);
                    }
                }
            });
            
            processedActivityIdsRef.current = cleaned;
            console.log('[NotificationCenter] Cleaned up old IDs, kept', cleaned.size, 'recent ones');
        };
        
        // Save processed IDs to localStorage
        const saveProcessedIds = () => {
            try {
                const idsArray = Array.from(processedActivityIdsRef.current);
                localStorage.setItem('processedNotificationIds', JSON.stringify(idsArray));
            } catch (error) {
                console.warn('[NotificationCenter] Failed to save processed IDs:', error);
            }
        };
        
        // Simple polling for dashboard notifications
        const pollForUpdates = async () => {
    try {
        console.log('[NotificationCenter] Polling for updates...');

        const headers = await getAuthHeaders();
        
        const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/recent-activity`, {
            headers: headers  // Use proper auth headers
        });
        
        if (!response.ok) {
            console.error('[NotificationCenter] Response not ok:', response.status);
            return;
        }

        const data = await response.json();
        console.log('[NotificationCenter] Received data:', data);
        
        if (data.activities && Array.isArray(data.activities)) {
            console.log(`[NotificationCenter] Processing ${data.activities.length} activities`);
            console.log(`[NotificationCenter] Currently have ${processedActivityIdsRef.current.size} processed IDs`);
            
            let newNotificationsCount = 0;
            
            data.activities.forEach((activity: any) => {
                const activityId = `${activity.type}_${activity.mapping_id}_${activity.timestamp}`;
                
                if (!processedActivityIdsRef.current.has(activityId)) {
                    console.log('[NotificationCenter] Adding new notification:', activity);
                    processedActivityIdsRef.current.add(activityId);
                    newNotificationsCount++;
                    
                    addNotification({
                        type: 'info',
                        title: 'New Activity Detected',
                        message: activity.message,
                        data: {
                            patientId: activity.patient_id,
                            mappingId: activity.mapping_id,
                            timestamp: activity.timestamp,
                            eventType: activity.type.toUpperCase(),
                        },
                    });
                } else {
                    console.log('[NotificationCenter] Skipping duplicate activity:', activityId);
                }
            });
            
            console.log(`[NotificationCenter] Added ${newNotificationsCount} new notifications`);
            if (newNotificationsCount > 0) saveProcessedIds();
        } else {
            console.log('[NotificationCenter] No activities found in response');
        }
    } catch (error) {
        console.error('[NotificationCenter] Failed to poll for updates:', error);
    }
};

        // Clean up old IDs on start
        cleanupOldIds();
        
        // Poll immediately once, then every 30 seconds
        pollForUpdates();
        const interval = setInterval(pollForUpdates, 30000);
        
        return () => {
            console.log('[NotificationCenter] Cleaning up polling interval');
            clearInterval(interval);
            // Save final state before cleanup
            saveProcessedIds();
        };
    }, []); 
    
    
    // Empty dependency array to run only once
// #####ADDED THIS FOR RECENT ACTIVITY ENDPOINT #####

    const handleClearAndRefresh = () => {
        // Clear notifications
        clearAll();
        
        // Clear the tracking to allow all notifications to show again
        localStorage.removeItem('processedNotificationIds');
        
        // Optional: Force a reload of the component to restart polling fresh
        window.location.reload();
    };
// ### ADDED THIS FOR RECENT ACTIVITY ENDPOINT #####
   const handleRefreshNotifications = () => {
    const pollForUpdates = async () => {
        try {
            console.log('[NotificationCenter] Manual refresh - polling for updates...');
            console.log('[NotificationCenter] Current processed IDs:', Array.from(processedActivityIdsRef.current));

            // Get auth headers
            const headers = await getAuthHeaders();
            
            const response = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/recent-activity`, {
                headers: headers  // Use proper auth headers instead of query params
            });

            if (!response.ok) {
                console.error('[NotificationCenter] Response not ok:', response.status);
                addNotification({
                    type: 'error',
                    title: 'Refresh Failed',
                    message: `Request failed with status ${response.status}`,
                    data: { eventType: 'REFRESH_ERROR' },
                });
                return;
            }

            const data = await response.json();
            console.log('[NotificationCenter] Received data:', data);

            if (data.activities && Array.isArray(data.activities)) {
                console.log(`[NotificationCenter] Processing ${data.activities.length} activities`);
                console.log(`[NotificationCenter] Currently have ${processedActivityIdsRef.current.size} processed IDs`);

                let newNotificationsCount = 0;

                data.activities.forEach((activity: any) => {
                    const activityId = `${activity.type}_${activity.mapping_id}_${activity.timestamp}`;
                    console.log('[NotificationCenter] Checking activity ID:', activityId);
                    console.log('[NotificationCenter] Activity patient_id:', activity.patient_id); // Debug log
                    console.log('[NotificationCenter] Is already processed?', processedActivityIdsRef.current.has(activityId));

                    if (!processedActivityIdsRef.current.has(activityId)) {
                        console.log('[NotificationCenter] Adding new notification:', activity);
                        processedActivityIdsRef.current.add(activityId);
                        newNotificationsCount++;

                        addNotification({
                            type: 'info',
                            title: 'New Activity Detected',
                            message: activity.message,
                            data: {
                                patientId: activity.patient_id,  // This should now work
                                mappingId: activity.mapping_id,
                                timestamp: activity.timestamp,
                                eventType: activity.type.toUpperCase(),
                            },
                        });
                    } else {
                        console.log('[NotificationCenter] Skipping duplicate activity:', activityId);
                    }
                });

                console.log(`[NotificationCenter] Added ${newNotificationsCount} new notifications`);

                // Save processed IDs
                if (newNotificationsCount > 0) {
                    try {
                        const idsArray = Array.from(processedActivityIdsRef.current);
                        localStorage.setItem('processedNotificationIds', JSON.stringify(idsArray));
                    } catch (error) {
                        console.warn('[NotificationCenter] Failed to save processed IDs:', error);
                    }
                }

                // Always show feedback
                if (newNotificationsCount > 0) {
                    addNotification({
                        type: 'success',
                        title: 'Refresh Complete',
                        message: `Added ${newNotificationsCount} new notifications`,
                        data: { eventType: 'REFRESH_RESULT' },
                    });
                } else {
                    addNotification({
                        type: 'info',
                        title: 'Refresh Complete',
                        message: `Found ${data.activities.length} activities, but all were already shown`,
                        data: { eventType: 'REFRESH_NO_NEW' },
                    });
                }
            } else {
                console.log('[NotificationCenter] No activities found in response');
                addNotification({
                    type: 'warning',
                    title: 'Refresh Complete',
                    message: 'No recent activities found',
                    data: { eventType: 'REFRESH_EMPTY' },
                });
            }
        } catch (error) {
            console.error('[NotificationCenter] Failed to poll for updates:', error);
            addNotification({
                type: 'error',
                title: 'Refresh Failed',
                message: 'Failed to fetch recent activities',
                data: { eventType: 'REFRESH_ERROR' },
            });
        }
    };

    pollForUpdates();
};
// ### ADDED THIS FOR RECENT ACTIVITY ENDPOINT #####

// Add this function for Force Refresh:

const handleForceRefreshNotifications = () => {
    console.log('[NotificationCenter] Force refresh - clearing processed IDs first...');
    
    // Clear the processed IDs to show all activities
    processedActivityIdsRef.current.clear();
    localStorage.removeItem('processedNotificationIds');
    
    // Add a notification to show we're doing a force refresh
    addNotification({
        type: 'info',
        title: 'Force Refresh',
        message: 'Cleared tracking and fetching all recent activities...',
        data: { eventType: 'FORCE_REFRESH_START' }
    });
    
    // Now call the regular refresh
    handleRefreshNotifications();
};

    return (
        <>
            <IconButton
                color="inherit"
                onClick={handleClick}
                data-tour={props['data-tour']}
                sx={{
                    position: 'relative',
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    },
                }}
            >
                <Badge badgeContent={unreadCount} color="error">
                    <NotificationsIcon />
                </Badge>
            </IconButton>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                PaperProps={{
                    sx: {
                        width: 450,
                        maxWidth: '90vw',
                        maxHeight: 600,
                        overflow: 'hidden',
                    },
                }}
            >
                <Paper elevation={0}>
                    {/* Header */}
                    <Box sx={{
                        p: 2,
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                    }}>
                        <Typography variant="h6" fontWeight="medium">
                            Notifications
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            {unreadCount > 0 && (
                                <Button
                                    size="small"
                                    startIcon={<MarkReadIcon />}
                                    onClick={markAllAsRead}
                                    sx={{ textTransform: 'none' }}
                                >
                                    Mark all read
                                </Button>
                            )}
                            {notifications.length > 0 && (
                                <Button
                                    size="small"
                                    startIcon={<DeleteIcon />}
                                    onClick={handleClearAndRefresh}
                                    color="error"
                                    sx={{ textTransform: 'none' }}
                                >
                                    Clear all
                                </Button>
                            )}
                            <Button
                                size="small"
                                startIcon={<RefreshIcon />}
                                onClick={handleRefreshNotifications}
                                variant="outlined"
                                sx={{ textTransform: 'none' }}
                            >
                                Refresh
                            </Button>
                        </Box>
                    </Box>

                    {/* Notifications List */}
                    <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                        {notifications.length === 0 ? (
                            <Box sx={{ p: 4, textAlign: 'center' }}>
                                <NotificationsIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                                <Typography variant="body2" color="text.secondary">
                                    No notifications yet
                                </Typography>
                            </Box>
                        ) : (
                            <List sx={{ p: 0 }}>
                                {notifications.map((notification, index) => (
                                    <React.Fragment key={notification.id}>
                                        <ListItem
                                            sx={{
                                                cursor: 'pointer',
                                                backgroundColor: notification.read
                                                    ? 'transparent'
                                                    : alpha(getNotificationColor(notification.type), 0.05),
                                                borderLeft: `4px solid ${notification.read
                                                    ? 'transparent'
                                                    : getNotificationColor(notification.type)}`,
                                                '&:hover': {
                                                    backgroundColor: alpha(theme.palette.action.hover, 0.1),
                                                },
                                            }}
                                            onClick={() => handleNotificationClick(notification)}
                                        >
                                            <ListItemIcon sx={{ minWidth: 40 }}>
                                                {getNotificationIcon(notification.type)}
                                            </ListItemIcon>
                                            <ListItemText
                                                primary={<Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                                    <Typography variant="subtitle2" fontWeight="medium">
                                                        {notification.title}
                                                    </Typography>
                                                    <Chip
                                                        label={notification.type.toUpperCase()}
                                                        size="small"
                                                        sx={{
                                                            height: 20,
                                                            fontSize: '0.7rem',
                                                            backgroundColor: alpha(getNotificationColor(notification.type), 0.1),
                                                            color: getNotificationColor(notification.type),
                                                        }} />
                                                </Box>}
                                                secondary={<Box>
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                                        {notification.message}
                                                    </Typography>

                                                    {/* Enhanced notification data display */}
                                                    {renderNotificationData(notification)}

                                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                                                        {formatDistanceToNow(notification.timestamp, { addSuffix: true })}
                                                    </Typography>
                                                </Box>} />
                                            <IconButton
                                                size="small"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    removeNotification(notification.id);
                                                } }
                                                sx={{ ml: 1 }}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </ListItem>
                                        {index < notifications.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </Box>
                </Paper>
            </Popover>
        </>
    );
}


